import type { ReactNode } from 'react'
import { Tag, safeHttpUrl } from '@loop3/ui'
import type { EnrichmentProfile } from '@/lib/types/universe'

type Obj = Record<string, unknown>

const isObj = (v: unknown): v is Obj =>
  typeof v === 'object' && v !== null && !Array.isArray(v)

/** Reads a dotted path out of jsonb, returning `undefined` when any step is missing. */
function get(obj: unknown, path: string): unknown {
  return path
    .split('.')
    .reduce<unknown>((cur, key) => (isObj(cur) ? cur[key] : undefined), obj)
}

const asText = (v: unknown): string | null =>
  typeof v === 'string' && v.trim()
    ? v.trim()
    : typeof v === 'number'
      ? String(v)
      : null

const asList = (v: unknown): Obj[] => (Array.isArray(v) ? v.filter(isObj) : [])

const asStrings = (v: unknown): string[] =>
  Array.isArray(v) ? v.flatMap((x) => asText(x) ?? []) : []

const year = (v: unknown) => asText(v)?.slice(0, 4) ?? null

const dayMonth = (v: unknown): string | null => {
  const t = asText(v)
  const d = t ? new Date(t) : null
  return d && !Number.isNaN(d.getTime())
    ? d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
    : null
}

const strengthLabel = (s: unknown) =>
  typeof s !== 'number'
    ? 'Weak'
    : s >= 0.7
      ? 'Strong'
      : s >= 0.4
        ? 'Medium'
        : 'Weak'

function Block({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="mt-4">
      <h3 className="eyebrow mb-2">{title}</h3>
      {children}
    </div>
  )
}

function Tags({ items }: { items: string[] }) {
  return items.length ? (
    <ul className="flex flex-wrap gap-1.5">
      {items.map((t) => (
        <li key={t}>
          <Tag>{t}</Tag>
        </li>
      ))}
    </ul>
  ) : null
}

function Footer({ p }: { p: EnrichmentProfile }) {
  const d = new Date(p.enrichedAt)
  const when = Number.isNaN(d.getTime())
    ? ''
    : ` ${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`
  return (
    <p className="mt-4 text-xs opacity-60">
      Enriched{when} · {p.schemaVersion}
    </p>
  )
}

function Shell({ children, p }: { children: ReactNode; p: EnrichmentProfile }) {
  return (
    <section className="mb-8 rounded-xl border border-border p-4">
      <h2 className="text-sm font-semibold">Profile</h2>
      {children}
      <Footer p={p} />
    </section>
  )
}

/** Enriched person profile: role, career, presence, our history with them and who knows them. */
export function PersonProfileSection({
  profile,
}: {
  profile: EnrichmentProfile | null
}) {
  if (!profile) return null
  const x = profile.profile
  const title = asText(get(x, 'role.title'))
  const company = asText(get(x, 'role.company_name'))
  const heading = [title, company].filter(Boolean).join(' · ')
  const tags = [get(x, 'role.seniority'), get(x, 'role.function')].flatMap(
    (v) => asText(v) ?? []
  )
  const location = asText(get(x, 'identity.location'))
  const summary = asText(get(x, 'identity.summary'))
  const experience = asList(get(x, 'career.experience'))
  const education = asList(get(x, 'career.education'))
  const presence = asList(get(x, 'presence.public_presence')).flatMap((i) => {
    const url = safeHttpUrl(asText(i.url))
    return url ? [{ url, label: asText(i.title) ?? url }] : []
  })
  const count = get(x, 'internal.interactions.count')
  const last = dayMonth(get(x, 'internal.interactions.last_at'))
  const people = asStrings(get(x, 'internal.interactions.loop3_people'))
  const topics = asStrings(get(x, 'internal.topics'))
  const connections = asList(get(x, 'network.connections'))
  const met =
    typeof count === 'number' && count > 0
      ? [
          `We met ${count} ${count === 1 ? 'time' : 'times'}`,
          last && `last ${last}`,
          people.length > 0 && `with ${people.join(', ')}`,
        ]
          .filter(Boolean)
          .join(' · ')
      : null

  return (
    <Shell p={profile}>
      {heading && <p className="mt-2 text-sm font-medium">{heading}</p>}
      {(tags.length > 0 || location) && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <Tags items={tags} />
          {location && <span className="text-sm opacity-70">{location}</span>}
        </div>
      )}
      {summary && <p className="mt-2 text-sm opacity-80">{summary}</p>}
      {met && <p className="mt-3 text-sm">{met}</p>}
      {topics.length > 0 && (
        <Block title="Topics">
          <Tags items={topics} />
        </Block>
      )}
      {experience.length > 0 && (
        <Block title="Career">
          <ul className="space-y-1.5 text-sm">
            {experience.map((e, i) => (
              <li key={i}>
                <span className="font-medium">{asText(e.company)}</span>
                {asText(e.title) && <span> · {asText(e.title)}</span>}
                {year(e.start) && (
                  <span className="opacity-60">
                    {' '}
                    · {year(e.start)}-{year(e.end) ?? 'now'}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </Block>
      )}
      {education.length > 0 && (
        <Block title="Education">
          <ul className="space-y-1.5 text-sm">
            {education.map((e, i) => (
              <li key={i}>
                <span className="font-medium">{asText(e.school)}</span>
                {asStrings(e.degrees).length > 0 && (
                  <span> · {asStrings(e.degrees).join(', ')}</span>
                )}
              </li>
            ))}
          </ul>
        </Block>
      )}
      {presence.length > 0 && (
        <Block title="Public presence">
          <ul className="space-y-1 text-sm">
            {presence.map((l) => (
              <li key={l.url}>
                <a
                  href={l.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline underline-offset-2 [overflow-wrap:anywhere]"
                >
                  {l.label}
                </a>
              </li>
            ))}
          </ul>
        </Block>
      )}
      {connections.length > 0 && (
        <Block title="Who knows them">
          <ul className="space-y-1.5 text-sm">
            {connections.map((c, i) => (
              <li key={i} className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{asText(c.name)}</span>
                <Tag>{strengthLabel(c.strength)}</Tag>
                <span className="opacity-60">
                  {asStrings(c.origins)
                    .map((o) => o.replace(/_/g, ' '))
                    .join(', ')}
                </span>
              </li>
            ))}
          </ul>
        </Block>
      )}
    </Shell>
  )
}

/** Enriched company profile: summary, HQ, industries, investor focus and signals. */
export function CompanyProfileSection({
  profile,
}: {
  profile: EnrichmentProfile | null
}) {
  if (!profile) return null
  const x = profile.profile
  const summary = asText(get(x, 'summary'))
  const hq = [get(x, 'hq.city'), get(x, 'hq.country')]
    .flatMap((v) => asText(v) ?? [])
    .join(', ')
  const founded = asText(get(x, 'founded_year'))
  const industries = asStrings(get(x, 'industries'))
  const thesis = asText(get(x, 'investor.thesis'))
  const investor: [string, string[]][] = [
    ['Stages', asStrings(get(x, 'investor.stages'))],
    ['Sectors', asStrings(get(x, 'investor.sectors'))],
    ['Geographies', asStrings(get(x, 'investor.geographies'))],
  ]
  const maturity = asText(get(x, 'signals.data_ai_maturity'))
  const open = get(x, 'signals.hiring.open_roles')
  const dataRoles = get(x, 'signals.hiring.data_or_engineering_roles')
  const news = asList(get(x, 'signals.recent_news')).flatMap((n) => {
    const url = safeHttpUrl(asText(n.url))
    return url ? [{ url, title: asText(n.title) ?? url }] : []
  })
  const people = asList(get(x, 'people.key_people'))

  return (
    <Shell p={profile}>
      {summary && <p className="mt-2 text-sm">{summary}</p>}
      {(hq || founded) && (
        <p className="mt-2 flex flex-wrap gap-x-2 text-sm opacity-70">
          {hq && <span>{hq}</span>}
          {founded && <span>Founded {founded}</span>}
        </p>
      )}
      {industries.length > 0 && (
        <Block title="Industries">
          <Tags items={industries} />
        </Block>
      )}
      {(thesis || investor.some(([, v]) => v.length > 0)) && (
        <Block title="Investor focus">
          {thesis && <p className="mb-2 text-sm">{thesis}</p>}
          {investor.map(([label, v]) =>
            v.length > 0 ? (
              <div
                key={label}
                className="mb-2 flex flex-wrap items-center gap-1.5"
              >
                <span className="text-sm opacity-60">{label}</span>
                <Tags items={v} />
              </div>
            ) : null
          )}
        </Block>
      )}
      {(maturity || typeof open === 'number' || news.length > 0) && (
        <Block title="Signals">
          <ul className="space-y-1 text-sm">
            {maturity && <li>Data and AI maturity: {maturity}</li>}
            {typeof open === 'number' && (
              <li>
                {open} open {open === 1 ? 'role' : 'roles'}
                {typeof dataRoles === 'number' &&
                  `, ${dataRoles} in data or engineering`}
              </li>
            )}
            {news.map((n) => (
              <li key={n.url}>
                <a
                  href={n.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline underline-offset-2 [overflow-wrap:anywhere]"
                >
                  {n.title}
                </a>
              </li>
            ))}
          </ul>
        </Block>
      )}
      {people.length > 0 && (
        <Block title="Key people">
          <ul className="space-y-1 text-sm">
            {people.map((k, i) => (
              <li key={i}>
                <span className="font-medium">{asText(k.name)}</span>
                {asText(k.title) && <span> · {asText(k.title)}</span>}
              </li>
            ))}
          </ul>
        </Block>
      )}
    </Shell>
  )
}
